import {
	IAnalyticsEventDetails,
	IEventConfig,
	TAnalyticsEventType,
} from '@shared/types';

import { PAGEVIEW } from '@/constants';

import { sendEventToFoxtrot } from './FoxtrotUtils';
import { sendEventToGarfield } from './GarfieldUtils';

interface IPageViewEvent {
	path: string;
	name: string;
}

interface IFilterObj {
	instruments: string[];
	status: string[];
}

const NO_PAGEVIEW_URLS = [
	'/',
	'/transactions',
	'/refunds',
	'/qr',
	'/store',
	'/reports',
];

export const formatFilterValueForEvent = (
	filterObj: IFilterObj,
	availableFilters = [],
) => {
	const { instruments = [], status = [] } = filterObj;
	const eventValue: {
		instrument_type?: string;
		status_type?: string;
		search_by?: string;
	} = {};

	const searchByFilter: string[] = [];
	availableFilters.forEach((filterName) => {
		if (filterObj[filterName]) {
			searchByFilter.push(filterName);
		}
	});

	if (instruments.length) {
		eventValue.instrument_type = instruments.join(', ');
	}

	if (status.length) {
		eventValue.status_type = status.join(', ');
	}

	if (searchByFilter.length) {
		eventValue.search_by = searchByFilter.join(', ');
	}

	return eventValue;
};

export const sendApiErrorEvent = (
	eventData: IAnalyticsEventDetails,
	config: Partial<IEventConfig> = {},
) => {
	const { isFoxtrotEnabled = true } = config;

	if (isFoxtrotEnabled) {
		sendEventToFoxtrot({
			eventType: 'apiError',
			eventData,
		});
	}
};

export const sendPageViewEvent = (
	eventData: IPageViewEvent,
	config: Partial<IEventConfig> = {},
) => {
	const { isGarfieldEnabled = true, isFoxtrotEnabled = true } = config;
	const { path, name } = eventData;
	/**
	 * NO_PAGEVIEW_URLS - to skip the Pageview events for such URLs e.g '/', '/transactions' etc
	 */
	if (NO_PAGEVIEW_URLS.includes(path)) return;

	const eventSchema = {
		eventType: 'pageview' as const,
		eventData: {
			name: name || PAGEVIEW,
			value: path,
		},
	};

	if (isGarfieldEnabled && window.garfield) {
		sendEventToGarfield(eventSchema);
	}

	if (isFoxtrotEnabled) {
		sendEventToFoxtrot(eventSchema);
	}
};

export const sendClickEvent = (
	eventDetails: IAnalyticsEventDetails,
	config: Partial<IEventConfig> = {},
) => {
	const { isGarfieldEnabled = true, isFoxtrotEnabled = true } = config;
	if (isGarfieldEnabled && window.garfield) {
		sendEventToGarfield({
			eventType: 'click',
			eventData: eventDetails,
		});
	}

	if (isFoxtrotEnabled) {
		sendEventToFoxtrot({
			eventType: 'click',
			eventData: eventDetails,
		});
	}
};

export const sendNetworkMetricEvent = (
	eventDetails: IAnalyticsEventDetails,
) => {
	sendEventToFoxtrot({
		eventType: 'networkMetric',
		eventData: eventDetails,
	});
};

export const sendCustomAnalyticsEvent = (
	eventType: TAnalyticsEventType,
	eventDetails: IAnalyticsEventDetails,
	config: Partial<IEventConfig> = {},
) => {
	const {
		isGarfieldEnabled = true,
		isFoxtrotEnabled = true,
		pushToQueue = false,
	} = config;
	if (isGarfieldEnabled && window.garfield) {
		sendEventToGarfield({
			eventType,
			eventData: eventDetails,
		});
	}

	if (isFoxtrotEnabled) {
		sendEventToFoxtrot({
			eventType,
			eventData: eventDetails,
			pushToQueue,
		});
	}
};
